<template>
  <div v-if="isResidentalRef" class="shop-lead-form">
    <div class="shop-lead-form-inner">
      <h2 class="shop-lead-form__title">{{ $t('shopLeadFormTitle') }}</h2>
      <div class="shop-lead-form__subtitle">{{ $t('shopLeadFormSubtitle') }}</div>

      <form @submit.prevent="submit" class="shop-lead-form__form">
        <component
          v-for="{ component = 'InputComponent', key, ...props } in fields"
          :is="component"
          :key="key"
          :data-field="key"
          v-model="data[key]"
          v-bind="props"
        />

        <ButtonComponent
          :label="$t('sendToEmail')"
          :success-label="$t('completed')"
          theme="primary"
          type="submit"
          :icon="['fal', 'angle-right']"
          :promise="promise"
        />
      </form>
      <AlertComponent v-if="submitted">
        {{ this.$t('shopLeadFormSuccessMessage') }}
      </AlertComponent>
    </div>
  </div>
</template>
<script>
import { InputComponent } from 'vue-elder-input'
import TextArea from '../components/TextArea.vue'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { AlertComponent } from 'vue-elder-alert'
import { Clone, convertSelectionToBase64 } from '../utils'

import { createLead } from '../api'

const defaultData = {
  name: '',
  email: '',
  phone: '',
  message: '',
  upsell: true,
  privacy: false,
}

export default {
  props: {
    shop: Object,
  },
  data() {
    return {
      data: Clone(defaultData),
      promise: null,
      submitted: false,
    }
  },
  methods: {
    reset() {
      this.data = Clone(defaultData)
    },
    submit() {
      const selections = convertSelectionToBase64(this.shop)

      const url = new URL(window.location.href)
      if (selections) url.searchParams.append('selections', selections)

      const data = {
        contact: {
          name: this.data.name,
          email: this.data.email,
          phone: this.data.phone,
        },
        comment: [
          this.$t('shopLeadFormComment'),
          `${this.$t('contactMeForMoreInfo')}: ${this.$t(this.data.upsell ? 'yes' : 'no')}`,
          this.data.message ? `${this.$tc('leadMessage', 1)}: ${this.data.message}` : null,
          `\n--*${this.$t('sentFromShop')}: [${this.$t('shopLeadLinkText')}](${url.toString()})*--`,
        ]
          .filter(Boolean)
          .join('\n'),
        attachments: [
          {
            name: `${this.$t('shopShort')} - ${
              this.shop.reference.name
            } - ${new Intl.DateTimeFormat('nb-NO').format(new Date())}`,
            type: 'custom/url',
            url: url.toString(),
            tags: ['shop-setup'],
          },
        ],
      }

      this.promise = createLead({
        ...data,
        scopes: [
          {
            ref: this.shop.reference.id,
            onModel: 'Residential',
          },
        ],
        reference: {
          ref: this.shop.project.id,
          onModel: 'Project',
        },
      }).then(() => {
        this.submitted = true
        this.reset()
      })
    },
  },
  computed: {
    isResidentalRef() {
      return this.$path('shop.reference.__typename') === 'Residential'
    },
    fields() {
      return [
        {
          key: 'name',
          type: 'text',
          label: this.$t('name'),
          required: true,
        },
        {
          key: 'email',
          type: 'email',
          required: true,
          label: this.$t('email'),
        },
        {
          key: 'phone',
          type: 'tel',
          required: false,
          label: this.$t('phone'),
        },
        {
          key: 'message',
          component: TextArea,
          required: false,
          label: this.$tc('leadMessage', 1),
          condition: this.data.upsell,
        },
        {
          key: 'upsell',
          label: this.$t('contactMeForMoreInfo'),
          component: CheckboxComponent,
        },
        {
          key: 'privacy',
          required: true,
          label: this.$t('iAcceptX', {
            x: `<a href="/api/legal/privacy/tenant" target="_blank">${this.$t(
              'privacy',
            ).toLowerCase()}</a>`,
          }),
          component: CheckboxComponent,
        },
      ].filter(field => !('condition' in field) || field.condition)
    },
  },
  components: {
    InputComponent,
    CheckboxComponent,
    AlertComponent,
  },
}
</script>

<style lang="scss">
@import '../mixins.scss';

.shop-lead-form {
  container-type: inline-size;

  &__title {
    margin: 0;
    line-height: 1;
  }

  &__subtitle {
    opacity: 0.8;
    margin-top: 0.25rem;
  }

  &__form {
    margin-top: 1rem;

    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem 1.5rem;
  }

  [data-field='name'],
  [data-field='message'],
  [data-field='privacy'],
  [data-field='upsell'] {
    grid-column: 1 / -1;
  }

  @container (max-width: 500px) {
    [data-field='email'],
    [data-field='phone'] {
      grid-column: 1 / -1;
    }
  }

  .elder-checkbox__box {
    border-color: rgba(black, 0.1);
  }

  .alert {
    background-color: rgba(black, 0.05);
    border-radius: var(--border-radius);
    overflow: clip;

    margin-top: 1rem;
    &__content {
      &:before {
        content: unset;
      }
    }
  }

  button {
    margin-top: 1rem;
    width: fit-content;
  }
}
</style>
